import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import CaseList from "../CaseList";
import CaseNavList from "../CaseNavList";
import CaseForm from "../CaseForm";
import { TableListLoader } from "../../../../components/Loader/TableListLoader";
import { FormLoader } from "../../../../components/Loader/FormLoader";
import {
  getLiabilityDetailById,
  getLiabilityListItems,
} from "../../../../redux/Case/liabilitySlice";
import {
  LIABILITY_CASE_TYPE_MAP,
  LIABILITY_HAS_STATUS_FIELD,
} from "./LiabilityHomePage";
import { getCurrentStatusParam } from "../../../../shared/caseActiveStatus";
import { useToastMessage } from "../../../../components/ToastMessage/ToastMessage";
import { Toaster } from "@fluentui/react-components";

interface LiabilityDetailPageProps {
  tab?: string;
}

const LiabilityDetailPage: React.FC<LiabilityDetailPageProps> = ({ tab }) => {
  const { clientId, case_type, action, caseId } = useParams();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const { showToast, toasterId } = useToastMessage();
  const [currentRecord, setCurrentRecord] = useState(1);
  const [showNonCurrent, setShowNonCurrent] = useState(false);
  const hasAutoSwitched = useRef(false);
  const [nonCurrentExists, setNonCurrentExists] = useState(false);

  const { activeItem, listItems, loading, activeItemloading } = useSelector(
    (state: RootState) => state.liabilities
  );

  const caseType = case_type || "";
  const dbCaseType = case_type ? LIABILITY_CASE_TYPE_MAP[case_type] : undefined;
  const hasStatusField = case_type ? LIABILITY_HAS_STATUS_FIELD[case_type] : true;

  useEffect(() => {
    setShowNonCurrent(false);
    setNonCurrentExists(false);
    hasAutoSwitched.current = false;
  }, [case_type]);

  useEffect(() => {
    if (action && action?.length > 5) {
      navigate(
        `/clients/${clientId}/liabilities/${case_type}/${action}/${tab || "liability"}/view`
      );
    }
    if (!action && caseId) {
      navigate(
        `/clients/${clientId}/liabilities/${case_type}/${caseId}/${tab || "liability"}/view`
      );
    }
  }, [action, caseId]);

  useEffect(() => {
    if (clientId) {
      dispatch(
        getLiabilityListItems({
          customerId: clientId,
          caseType: dbCaseType,
          status:
            hasStatusField && !showNonCurrent
              ? getCurrentStatusParam("liability")
              : undefined,
        })
      ).then((result: any) => {
        if (hasStatusField && !showNonCurrent && !hasAutoSwitched.current) {
          const items = Array.isArray(result?.payload) ? result.payload : [];
          if (items.length === 0) {
            hasAutoSwitched.current = true;
            setNonCurrentExists(true);
            setShowNonCurrent(true);
          }
        }
      });
    }
  }, [clientId, dbCaseType, hasStatusField, showNonCurrent, dispatch]);

  useEffect(() => {
    if (caseId && clientId) {
      dispatch(getLiabilityDetailById({ id: caseId, customerId: clientId }));
    }
  }, [caseId, clientId, dispatch]);

  const caseTypeListItems = useMemo(() => {
    return dbCaseType
      ? listItems.filter((item: any) => item.case_type === dbCaseType)
      : listItems;
  }, [listItems, dbCaseType]);

  const isEmptyCurrentView =
    nonCurrentExists && !showNonCurrent && caseTypeListItems.length === 0;

  return (
    <>
      <Toaster toasterId={toasterId} position="top-end" timeout={5000} />
      {action && (
        <CaseNavList
          menuItem={undefined}
          section="liabilities"
          tab={tab}
          caseType={caseType}
          caseDetail={activeItem}
          mode={action || ""}
        />
      )}
      <div className="case_container_flex">
        {action && !isEmptyCurrentView &&
          (activeItemloading ? (
            <FormLoader />
          ) : (
            <CaseForm
              mode={action || ""}
              section="liabilities"
              caseType={caseType}
              caseDetail={activeItem}
              caseItemList={caseTypeListItems}
              tab={tab}
              notify={showToast}
              currentRecord={currentRecord}
              setCurrentRecord={setCurrentRecord}
            />
          ))}
        <div>
          {loading ? (
            <TableListLoader rows={8} columns={6} />
          ) : (
            action &&
            (caseTypeListItems.length > 0 || nonCurrentExists) && (
              <CaseList
                module="liabilities"
                data={caseTypeListItems}
                activeItem={activeItem}
                section="liabilities"
                tab={tab}
                currentRecord={currentRecord}
                setCurrentRecord={setCurrentRecord}
                hasStatusField={hasStatusField}
                showNonCurrent={showNonCurrent}
                onToggleNonCurrent={() => setShowNonCurrent((prev) => !prev)}
                emptyMessage={
                  isEmptyCurrentView
                    ? 'No active records for current cases. Click "Non-current Cases" to view non-current records.'
                    : undefined
                }
              />
            )
          )}
        </div>
      </div>
    </>
  );
};

export default LiabilityDetailPage;
